import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useApiClient } from "../../../apiClient";
import { primeQueryCache } from "../../../utils/primeQueryCache";
import { OrganizationLocationDto } from "../../../models/location/dto/location.dto";
import { GET_LOCATION_QUERY_KEY } from "./useGetLocation";

export const GET_LOCATIONS_QUERY_KEY = (orgId: string) => [orgId, 'locations'];
export const useGetLocations = (organizationId: string) => {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const result = useQuery({
        // Every caller passes `selectedOrganization?.id`, which is `undefined`
        // until `GET my-organizations` resolves — and one of them is `SearchBar`
        // in the navbar, mounted on every admin page. Ungated, the first paint
        // of each page requested `organizations/undefined/locations` (a 200 with
        // an empty array, so nothing ever failed loudly enough to be noticed).
        enabled: Boolean(organizationId),
        staleTime: 0, /** removing the cache for now so the location page gets updated when `back to clubs` */
        cacheTime: 0, /** useGetLocations could have a setCache param later */
        // NO `placeholderData` (removed run 107, with its exemption in
        // `list-query-placeholder-gate.test.ts`, whose stated premise was
        // "remove it once #567 merges" — #567 merged 2026-09-22).
        //
        // It made this query report `status: 'success'`, `isLoading: false`
        // and `data: []` even while DISABLED (measured against the real
        // library, not read from the docs), which had two effects worth naming
        // so nobody restores it as a convenience:
        //   - `LocationListPage` rendered `locations.no_locations` — "this
        //     organization has no clubs" — for a read that was never issued.
        //   - every downstream `isLoading` on this query was dead code, so the
        //     four consumers that read one were untested in the state they
        //     were written for. They now take `isInitialLoading` (D62), which
        //     is false for a disabled query and true only while a request is
        //     actually in flight.
        queryKey: GET_LOCATIONS_QUERY_KEY(organizationId),
        queryFn: () => client.fetchApi<OrganizationLocationDto[], {}>({ endpoint: `organizations/${organizationId}/locations`, method: 'GET' }),
    });

    useEffect(() => {
        // O(locations), not O(locations x cache) — see `primeQueryCache`.
        //
        // This was the SIXTH and last instance of the per-item priming loop
        // `1f1d88d99` swept from five list hooks and `20363ff3a` removed from
        // `useGetUsers`. It was missed because that sweep's population was
        // "list hooks that prime in `onSuccess`" and this one primes in a
        // `useEffect`. It is also the worst-placed one: `SearchBar` in the
        // navbar calls this hook on EVERY admin page, and `cacheTime: 0`
        // above means the query refetches on every mount, so the loop ran
        // once per navigation against a cache the users roster inflates with
        // thousands of entries that live their 5-minute `cacheTime`.
        // Measured here with 4,000 unrelated entries cached and 300
        // locations: 2,067 ms before, 69 ms after — and most of that 69 ms is
        // the test harness's own render and `waitFor` polling
        // (`useGetLocations.primeCost.test.ts`).
        primeQueryCache(queryClient, (item: OrganizationLocationDto) => GET_LOCATION_QUERY_KEY(item.id), result?.data);
    }, [result?.data, queryClient]);

    return result;
};